import feathers from '@feathersjs/client';
import { AppLoader } from 'feathers-dataloader';
import stateContainer from 'src/lib/state';

import auth from './auth';
import { convertFormikError } from './hooks';
import logRocket from './logRocket';
import { disablePaginationPlugin, findOnePlugin } from './plugins';
import reactQuery from './reactQuery';
import sentry from './sentry';
import addressDetails from './services/address-details/address-details.service';
import contact from './services/contact/contact.service';
import documents from './services/documents/documents.service';
import directions from './services/google/directions';
import placePredictions from './services/google/place-predictions';
import places from './services/google/places';
import invitations from './services/invitations/invitations.service';
import paymentMethods from './services/payment-methods/payment-methods.service';
import shipmentInvoicePreviews from './services/shipment-invoice-previews/shipment-invoice-previews.service';
import shipmentInvoiceStats from './services/shipment-invoice-stats/shipment-invoice-stats.service';
import shipmentInvoices from './services/shipment-invoices/shipment-invoices.service';
import shipmentParses from './services/shipment-parses/shipment-parses.service';
import shipmentPreviews from './services/shipment-previews/shipment-previews.service';
import shipmentQuoteOffers from './services/shipment-quote-offers/shipment-quote-offers.service';
import shipmentQuotes from './services/shipment-quotes/shipment-quotes.service';
import shipmentReports from './services/shipment-reports/shipment-reports.service';
import shipmentRoutes from './services/shipment-routes/shipment-routes.service';
import shipmentStats from './services/shipment-stats/shipment-stats.service';
import shipmentStatusChanges from './services/shipment-status-changes/shipment-status-changes.service';
import shipmentTracking from './services/shipment-tracking/shipment-tracking.service';
import shipments from './services/shipments/shipments.service';
import shipper from './services/shipper/shipper.service';
import signup from './services/signup/signup.service';
import user from './services/user/user.service';
import users from './services/users/users.service';
import weather from './services/weather/weather.service';
import socketio from './socketio';
import storage from './storage';

const app = feathers();

// Because sentry handles all uncaught exceptions,
// enable it before anything else
app.configure(sentry);

app.configure(socketio);

app.configure(disablePaginationPlugin);
app.configure(findOnePlugin);

app.configure((app) => {
  app.mixins.push((service) => {
    service.timeout = 1000 * 30;
  });
});

// Automagically prepend all api/* routes with
// shipper-api/* to encourage use of the proper endpoints
app.configure((app) => {
  const oldService = app.service;
  app.service = function (path, ...args) {
    const finalPath = path.startsWith('api') ? `shipper-${path}` : path;
    return oldService.call(this, finalPath, ...args);
  };
});

app.configure(storage);
app.configure(auth);
app.configure(logRocket);
app.configure(reactQuery);

// Services/Hooks
app.configure(shipments);
app.configure(signup);
app.configure(shipmentPreviews);
app.configure(shipmentQuotes);
app.configure(users);
app.configure(invitations);
app.configure(shipper);
app.configure(user);
app.configure(contact);
app.configure(paymentMethods);
app.configure(documents);
app.configure(shipmentInvoices);
app.configure(shipmentInvoicePreviews);
app.configure(shipmentInvoiceStats);
app.configure(shipmentTracking);
app.configure(shipmentStats);
app.configure(shipmentRoutes);
app.configure(addressDetails);
app.configure(shipmentReports);
app.configure(shipmentStatusChanges);
app.configure(shipmentQuoteOffers);
app.configure(shipmentParses);

app.configure(weather);

app.configure(directions);
app.configure(placePredictions);
app.configure(places);

// App state container
app.configure((app) => {
  app.set('stateContainer', stateContainer);
});

const initializeLoader = (context) => {
  if (context.params.loader) {
    return context;
  }
  context.params.loader = new AppLoader({ app: context.app });
  return context;
};

// After all calls, if is not authed, delete the token
// and redirect to the login page with a full page reload
// to purge any data in memory.
const redirectUnAuthed = async (context) => {
  if (context.error.code === 401 && !context.path.startsWith('auth')) {
    await context.app.cleanup();
  }
  return context;
};

// Hooks run on all services
app.hooks({
  before: { all: [initializeLoader] },
  error: { all: [redirectUnAuthed, convertFormikError] }
});

app.configure((app) => {
  app.prep = async function () {
    const promises = [
      app
        .service('api/shipper')
        .find()
        .then((result) => {
          stateContainer.setInState('shipper', result);
        }),
      app
        .service('api/user')
        .find()
        .then((result) => {
          stateContainer.setInState('user', result);
        })
    ];
    return Promise.all(promises).then(() => {
      app.get('stateContainer').setInState('prepped', true);
    });
  };
});

export default app;
